/**
 * The tenant's accent colour, as design tokens.
 *
 * `Settings → Organisation → Accent colour` stores a plain `#rrggbb` value.
 * It has to be applied at runtime rather than compiled into the stylesheet,
 * because it is per-tenant data: the tokens are set as inline custom
 * properties on `<html>`, which is what lets a workspace recolour the product
 * without a release.
 *
 * `--primary` is the token every button, link and focus ring is built from;
 * the sidebar and first chart series follow so the whole surface moves
 * together. The matching foreground is computed rather than fixed to white,
 * because the palette runs from indigo to amber and white text on the amber
 * swatch is unreadable — but white is the default the rule falls back to, so
 * that every ordinary brand colour (indigo, red, violet, blue) keeps the white
 * label it had before the token was overridden at runtime.
 */

export const ACCENT_TOKENS = [
  '--primary',
  '--ring',
  '--sidebar-primary',
  '--sidebar-ring',
  '--chart-1',
] as const;

export const ACCENT_FOREGROUND_TOKENS = [
  '--primary-foreground',
  '--sidebar-primary-foreground',
] as const;

const ALL_TOKENS = [...ACCENT_TOKENS, ...ACCENT_FOREGROUND_TOKENS];

/** Stored accents are validated as `#rrggbb` on the way in; be defensive anyway. */
const HEX = /^#[0-9a-fA-F]{6}$/;

/**
 * WCAG relative luminance, 0 (black) to 1 (white).
 */
export function luminance(hex: string): number {
  const [r, g, b] = [1, 3, 5].map((offset) => {
    const channel = Number.parseInt(hex.slice(offset, offset + 2), 16) / 255;
    return channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];

  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * Luminance above which a swatch is too light to carry a white label.
 *
 * Picking the label at the black/white crossover (0.179, where the two have
 * equal contrast) flipped the text on swatches that are still plainly dark:
 * indigo (0.185), violet (0.215) and red (0.229) all sit just above it, so the
 * product's own action buttons were being labelled in near-black while the
 * compiled-in primary used white.
 *
 * 0.4 was the obvious repair but still catches sky blue (0.329), which reads
 * as a coloured button. 0.34 separates the shipped palette exactly where the
 * interface expects it to: indigo, violet, red and sky keep a white label;
 * emerald (0.364) and amber (0.439) take dark text.
 *
 * This is a product convention, not a contrast optimum. On sky blue white
 * reaches only 2.77:1 where dark text would reach 7.58:1 — the swatches are
 * listed in `ACCENTS` (settings-page.tsx), and the boundary is a single
 * constant, so moving it is one edit if the palette changes.
 */
const DARK_LABEL_ABOVE = 0.34;

/** The label colour that stays readable on top of `hex`. */
export function accentForeground(hex: string): string {
  return luminance(hex) > DARK_LABEL_ABOVE ? '#0b0b0c' : '#ffffff';
}

/** Set — or, with no colour, clear — the accent on the document root. */
export function applyAccentColor(color: string | undefined): void {
  const root = document.documentElement;

  if (!color || !HEX.test(color)) {
    for (const token of ALL_TOKENS) {
      root.style.removeProperty(token);
    }
    return;
  }

  for (const token of ACCENT_TOKENS) {
    root.style.setProperty(token, color);
  }

  const foreground = accentForeground(color);
  for (const token of ACCENT_FOREGROUND_TOKENS) {
    root.style.setProperty(token, foreground);
  }
}
